// Cottage / house panel — tier status, furniture list, guest ACL management,
// upgrade controls. Opens via /cottage UI or the account widget's house badge.

import type { ClientStore } from "../state.js";
import type { Net } from "../net.js";
import { openPanel } from "./shared_panel.js";

export function openCottagePanel(store: ClientStore, net: Net): void {
  // Prime the panel with fresh server data.
  net.send({ t: "house_status" });
  net.send({ t: "cottage_list" });
  net.send({ t: "cottage_guests" });
  openPanel({
    id: "panel-cottage",
    title: "Your House",
    store,
    render(state) {
      const h = state.house;
      const c = state.cottage;
      if (!h) {
        return `<div class="muted">Loading house info… (tip: /home to enter; requires subscription).</div>`;
      }
      const tierRow = `
        <div class="panel-block">
          <h3>${escape(h.tier_name)}</h3>
          <p class="muted">${escape(h.description)}</p>
          <div class="grid-form">
            <label>Tier</label><div><span class="badge tier-${h.tier}">${h.tier.toUpperCase()}</span></div><div></div>
            <label>Placeable bonus</label><div class="muted">+${h.placeable_bonus}</div><div></div>
            <label>Furniture used</label><div class="muted">${c.placed} / ${c.capacity}</div><div></div>
            <label>Guest cap</label><div class="muted">${h.guest_cap} concurrent</div><div></div>
          </div>
        </div>`;
      const nextRow = h.next ? `
        <div class="panel-block">
          <h3>Next tier: ${escape(h.next.name)}</h3>
          <div class="grid-form">
            <label>Gold cost</label><div class="${h.next.has_gold ? "ok" : "bad"}">${h.next.cost_gold}</div><div></div>
            <label>Token cost</label><div class="${h.next.has_tokens ? "ok" : "bad"}">${h.next.cost_tokens}</div><div></div>
            <label>Tenure required</label><div class="${h.next.has_tenure ? "ok" : "bad"}">${h.next.min_tenure_days} days</div><div></div>
          </div>
          <button id="btn-upgrade" ${(h.next.has_gold && h.next.has_tokens && h.next.has_tenure) ? "" : "disabled"}>Upgrade to ${escape(h.next.name)}</button>
        </div>` : `<div class="panel-block"><h3>Maxed</h3><p class="muted">Your home is at the highest tier.</p></div>`;
      const furniture = c.furniture.length
        ? c.furniture.map((f) => `<tr>
            <td class="muted">#${f.id}</td>
            <td>${escape(f.name)}${f.note ? ` <span class="muted">— ${escape(f.note)}</span>` : ""}</td>
            <td><button data-unplace="${f.id}" class="danger">Remove</button></td>
          </tr>`).join("")
        : `<tr><td colspan="3" class="muted">No furniture placed yet.</td></tr>`;
      const guests = c.guests.length
        ? c.guests.map((g) => `<tr>
            <td>${escape(g.name)}</td>
            <td class="muted">${g.expires_at > 0 ? `until ${new Date(g.expires_at).toISOString().slice(0, 10)}` : "permanent"}</td>
            <td><button data-revoke="${escape(g.name)}" class="danger">Revoke</button></td>
          </tr>`).join("")
        : `<tr><td colspan="3" class="muted">No guests invited.</td></tr>`;
      return `
        <style>
          .panel-block { background:var(--panel,#121520); border:1px solid var(--line,#2a2f3d); border-radius:6px; padding:12px 14px; margin-bottom:12px; }
          .panel-block h3 { color:var(--gold,#d9b150); font-size:13px; margin:0 0 8px; text-transform: uppercase; letter-spacing:0.08em; }
          .panel-block table { width:100%; border-collapse: collapse; font-size:12px; }
          .panel-block td, .panel-block th { padding:4px 6px; border-bottom:1px solid var(--line,#2a2f3d); text-align:left; }
          .panel-block .ok { color:#5fb878; }
          .panel-block .bad { color:#d96a6a; }
          .panel-block .grid-form { display:grid; grid-template-columns: 140px 1fr; gap:4px 12px; font-size:12px; }
          .panel-block button { font:inherit; font-size:11px; padding:2px 8px; }
          .panel-block .invite-row { display:flex; gap:6px; margin-bottom:6px; }
          .panel-block .invite-row input { flex:1; }
          .badge { padding:1px 6px; border-radius:3px; font-size:10px; letter-spacing:0.05em; }
          .badge.tier-cottage { background:#24252c; color:var(--dim,#7c8194); }
          .badge.tier-manor { background:rgba(120,180,240,0.15); color:#8ab8e8; }
          .badge.tier-keep { background:rgba(240,200,100,0.15); color:#e8c878; }
          .badge.tier-castle { background:rgba(240,100,100,0.2); color:#ff8080; }
        </style>
        ${tierRow}
        ${nextRow}
        <div class="panel-block">
          <h3>Furniture (${c.placed}/${c.capacity})</h3>
          <table><thead><tr><th>ID</th><th>Item</th><th></th></tr></thead><tbody>${furniture}</tbody></table>
          <div style="margin-top:8px;display:flex;gap:6px;align-items:center;">
            <select id="place-item" style="flex:1;">
              <option value="0">Place furniture from inventory...</option>
              ${(state.inventory ?? []).filter((i) => !i.equipped_slot).map((i) => `<option value="${i.id}">${escape(i.name)}</option>`).join("")}
            </select>
            <input id="place-note" type="text" placeholder="Note (optional)" style="width:140px;" />
            <button id="btn-place">Place</button>
          </div>
        </div>
        <div class="panel-block">
          <h3>Guests (${c.guests.length}/${h.guest_cap})</h3>
          <div class="invite-row">
            <input id="inv-name" placeholder="Character name to invite" />
            <button id="btn-invite">Invite</button>
            <button id="btn-visit" title="Visit someone else's cottage">Visit…</button>
          </div>
          <table><thead><tr><th>Name</th><th>Expires</th><th></th></tr></thead><tbody>${guests}</tbody></table>
        </div>`;
    },
    bind(root) {
      root.querySelector<HTMLButtonElement>("#btn-upgrade")?.addEventListener("click", () => {
        if (!confirm("Spend the upgrade cost now?")) return;
        net.send({ t: "house_upgrade" });
      });
      root.querySelectorAll<HTMLButtonElement>("button[data-unplace]").forEach((b) => {
        b.addEventListener("click", () => {
          if (!confirm("Remove this furniture? It returns to your inventory.")) return;
          net.send({ t: "cottage_remove", furniture_id: Number(b.dataset.unplace) });
        });
      });
      root.querySelectorAll<HTMLButtonElement>("button[data-revoke]").forEach((b) => {
        b.addEventListener("click", () => {
          const name = b.dataset.revoke ?? "";
          if (!confirm(`Revoke ${name}'s visit pass?`)) return;
          net.send({ t: "cottage_uninvite", guest: name });
        });
      });
      root.querySelector<HTMLButtonElement>("#btn-place")?.addEventListener("click", () => {
        const inst = Number(root.querySelector<HTMLSelectElement>("#place-item")?.value ?? 0);
        const note = root.querySelector<HTMLInputElement>("#place-note")?.value?.trim() ?? "";
        if (!inst) return;
        net.send({ t: "cottage_place", instance_id: inst, note });
      });
      root.querySelector<HTMLButtonElement>("#btn-invite")?.addEventListener("click", () => {
        const input = root.querySelector<HTMLInputElement>("#inv-name");
        const name = (input?.value ?? "").trim();
        if (!name) return;
        net.send({ t: "cottage_invite", guest: name });
        if (input) input.value = "";
      });
      root.querySelector<HTMLButtonElement>("#btn-visit")?.addEventListener("click", () => {
        const host = prompt("Which host's cottage to visit?"); if (!host) return;
        net.send({ t: "cottage_visit", host });
      });
    },
  });
}

function escape(s: string): string {
  return (s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" }[c]!));
}
